import { types, getParent, type IAnyModelType } from 'mobx-state-tree';
import { modelReference } from '../refHelper';
import AttributeOwner from './attribute_owner';
import Inheritance from './inheritance';
import type { IMetaModel } from './index';
import type { IInheritance } from './inheritance';
import type { IMethod } from './method';
import type { IObjectType } from './object_type';
import type { IAttribute } from './attribute';

/**
 * The EventType instance interface.
 *
 * HAND-DECLARED so the loose `modelReference` targets read as concrete instances,
 * the self-referencing `ownerEventType` views type, and sibling views see each
 * other.
 */
export interface IEventType {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly ownerEventType: IEventType | undefined;
  readonly inheritance: IInheritance | undefined;
  readonly attributes: IAttribute[];
  asString(): string;
  readonly displayString: string;
  readonly metamodel: IMetaModel;
  readonly specialised: boolean;
  readonly specialisationLevel: number;
  readonly specialisations: IEventType[];
  readonly methods: IMethod[];
  readonly acquiredMethods: IMethod[];
  readonly ownedMethods: IMethod[];
  readonly inheritedMethods: IMethod[];
  readonly specialisedMethods: IMethod[];
  readonly owningObjectTypes: IObjectType[];
  getAttributeById(id: string): IAttribute | undefined;
  setName(name: string): void;
  setDescription(description: string): void;
  specialise(ownerEventTypeId: string, inheritanceId: string): void;
  convertToNormal(): void;
  removeAttribute(id: string): void;
  addAttribute(
    id: string,
    name: string,
    type: string,
    description: string,
    implementation: string,
    validation: string
  ): IAttribute;
}

/**
 * A MERODE event type.
 *
 * This is only the 'base' for the MST model. It gets composed with the
 * AttributeOwner partial type below.
 */
const EventTypeBase = types
  .model('EventTypeBase', {
    id: types.identifier,
    name: types.string,
    description: types.optional(types.string, ''),
    ownerEventType: types.maybe(
      modelReference(types.late((): IAnyModelType => EventType))
    ),
    inheritance: types.maybe(
      modelReference(types.late((): IAnyModelType => Inheritance))
    ),
  })
  .views((self0) => {
    const self = self0 as unknown as IEventType;
    return {
    // Human-readable label, e.g. for `${event}` interpolation in messages.
    // (MST 7 forbids declaring a view named `toString`, so this is a plain view.)
    asString() {
      return self.name;
    },
    // Used in the logger and the report view
    get displayString() {
      return `Event ${self.name}`;
    },
    // convenience method to get the metamodel
    get metamodel(): IMetaModel {
      // up 2-levels because 1 level up is the array of event types.
      return getParent(self0, 2);
    },
    // Is this a specialised event type?
    get specialised() {
      return self.ownerEventType != null;
    },
    // How deep is the event in the specialisation tree
    get specialisationLevel(): number {
      if (!self.specialised) {
        return 0;
      } else {
        return self.ownerEventType!.specialisationLevel + 1;
      }
    },
    // Get all events that specialise this event
    get specialisations() {
      return self.metamodel.eventTypes.filter((e) => e.ownerEventType === self);
    },
    // all methods that have this event as their ownerEventType
    get methods() {
      return self.metamodel.methods.filter((m) => m.ownerEventType === self);
    },
    // all acquired methods that have this event as their ownerEventType
    get acquiredMethods() {
      return self.methods.filter((m) => m.provenance === 'ACQUIRED');
    },
    // all owned methods that have this event as their ownerEventType
    get ownedMethods() {
      return self.methods.filter((m) => m.provenance === 'OWNED');
    },
    // all inherited methods that have this event as their ownerEventType
    get inheritedMethods() {
      return self.methods.filter((m) => m.provenance === 'INHERITED');
    },
    // all specialised methods that have this event as their ownerEventType
    get specialisedMethods() {
      return self.methods.filter((m) => m.provenance === 'SPECIALISED');
    },
    // The 'ownerObjectType' for all owned or specialised methods that have this event as their
    // ownerEventType.
    get owningObjectTypes() {
      return self.ownedMethods.concat(self.specialisedMethods).map((m) => m.ownerObjectType);
    },
    };
  })
  .actions((self) => ({
    setName(name: string) {
      self.name = name;
    },
    setDescription(description: string) {
      self.description = description;
    },
    specialise(ownerEventTypeId: string, inheritanceId: string) {
      // MST accepts an identifier string when writing a reference, but the TS
      // write type is the resolved instance, so we cast at the assignment.
      self.ownerEventType = ownerEventTypeId as unknown as typeof self.ownerEventType;
      self.inheritance = inheritanceId as unknown as typeof self.inheritance;
    },
    convertToNormal() {
      self.ownerEventType = undefined;
      self.inheritance = undefined;
    },
  }));

// compose the base with Attribute Owner
const EventType = types.compose('EventType', EventTypeBase, AttributeOwner);

export default EventType;
